/**
 * Befindlichkeitsstatus (Anf. 10).
 *
 * ## Die Liste ist vorgegeben, nicht erfunden
 * Die sieben Einträge stehen wörtlich in der Anforderung. Sie sind bewusst
 * nicht erweiterbar: Eine lange Auswahl ist keine Freiheit, sondern eine
 * Entscheidung, die man jedes Mal treffen muss. Wer etwas Eigenes sagen will,
 * nimmt den freien Text — dafür ist er da.
 *
 * ## Die Grenze, die Anf. 10 zieht
 * > „Diese Funktion darf NICHT automatisch medizinische Diagnosen erstellen.
 * >  Keine medizinischen Schlussfolgerungen aus Texten, Emojis oder
 * >  Standortdaten ziehen."
 *
 * Das ist keine Absichtserklärung, sondern eine Bauvorgabe. Was daraus folgt,
 * steht in `AUSDRUECKLICH_NICHT` weiter unten — als ausführbare Dokumentation,
 * damit die Begründung nicht verlorengeht, wenn jemand eine „nützliche
 * Auswertung" ergänzen will.
 */
/**
 * Reihenfolge wie in Anf. 10: von „gut" zu „Hilfe".
 *
 * Nicht nach Dringlichkeit sortiert, obwohl das naheläge. Eine Liste, die mit
 * „Hilfe benötigt" beginnt, macht aus einer beiläufigen Frage („wie geht's?")
 * eine dramatische — und wer nur sagen will, dass er müde ist, scrollt daran
 * vorbei.
 */
export const STATUS_OPTIONEN = [
    { key: 'good', icon: '😊', label: 'Mir geht es gut', auffaellig: false },
    { key: 'ok', icon: '🙂', label: 'Alles okay', auffaellig: false },
    { key: 'stressed', icon: '😐', label: 'Etwas gestresst', auffaellig: false },
    { key: 'tired', icon: '😴', label: 'Müde', auffaellig: false },
    { key: 'unwell', icon: '🤒', label: 'Nicht so gut', auffaellig: true },
    { key: 'need_support', icon: '❤️', label: 'Brauche etwas Unterstützung', auffaellig: true },
    { key: 'help', icon: '🚨', label: 'Hilfe benötigt', auffaellig: true },
];
export const STATUS_KEYS = STATUS_OPTIONEN.map((o) => o.key);
/** Die Schlüssel, die auf der Startseite nach oben rücken. */
export const STATUS_AUFFAELLIG = STATUS_OPTIONEN.filter((o) => o.auffaellig).map((o) => o.key);
export function statusOption(key) {
    return STATUS_OPTIONEN.find((o) => o.key === key) ?? null;
}
/**
 * Was diese Funktion ausdrücklich NICHT tut (Anf. 10).
 *
 * Steht als ausführbare Dokumentation hier, im selben Sinn wie
 * `DELIBERATELY_ABSENT` in `permissions.ts`: Wer eine dieser Auswertungen
 * einbauen will, stößt zuerst auf diese Liste und ihre Begründung.
 *
 * Der Unterschied zwischen „wir werten nichts aus" und „es gibt nichts
 * auszuwerten" ist der ganze Punkt. Deshalb hat der Verlauf eine kurze Frist
 * und es gibt keinen Endpunkt, der über Zeiträume zusammenfasst.
 */
export const AUSDRUECKLICH_NICHT = {
    aggregation: 'Keine Statistik über Zeiträume — kein „Lena war diese Woche 4× nicht so gut". ' +
        'Eine Häufung ist eine medizinische Schlussfolgerung, und die verbietet Anf. 10.',
    trend: 'Keine Verlaufskurven, keine Stimmungsdiagramme. Ein Diagramm behauptet einen ' +
        'Zusammenhang, den niemand geprüft hat.',
    ableitung: 'Keine Verknüpfung von Status mit Standort oder Aktivität. „War beim Arzt und ' +
        'danach nicht so gut" ist eine Diagnose, die niemand gestellt hat.',
    benachrichtigung: 'Kein automatischer Alarm bei einem Status. Wer Hilfe braucht, nimmt SOS — ' +
        'ein Status, der von selbst eskaliert, hält Leute davon ab, ihn ehrlich zu setzen.',
};
/**
 * Wie lange ein Verlaufseintrag aufgehoben wird.
 *
 * Sieben Tage beantworten „was war diese Woche los?" und sind zu kurz für ein
 * Profil. Die Frist ist der Schutz: Was nicht da ist, kann niemand auswerten —
 * auch nicht jemand, der später auf die Idee kommt.
 */
export const VERLAUF_TAGE = 7;
/** Höchstlänge des freien Textes. */
export const STATUS_TEXT_MAX = 140;
/**
 * Ab wann ein Status alt ist.
 *
 * „Nicht so gut" von vorgestern ist keine Information, sondern ein
 * Missverständnis in Wartestellung. Nach zwei Tagen zeigt die Startseite ihn
 * nicht mehr hervorgehoben an.
 */
export const STATUS_FRISCH_STUNDEN = 48;
export function statusFrisch(statusAt, jetzt = Date.now()) {
    if (!statusAt)
        return false;
    return jetzt - new Date(statusAt).getTime() < STATUS_FRISCH_STUNDEN * 3600_000;
}
/** Wie lange nach dem letzten Lebenszeichen jemand noch als „online" gilt. */
export const PRESENCE_ONLINE_MINUTEN = 5;
/**
 * Anwesenheit in Alltagssprache.
 *
 * „Zuletzt online: 14:32" ist eine Uhrzeit, die niemand einordnen kann, wenn
 * er nicht weiß, wie spät es ist. „Vor 20 Minuten" ist sofort verständlich.
 */
export function anwesenheitsText(presence, lastSeenAt, jetzt = Date.now()) {
    if (presence === 'online')
        return 'Jetzt online';
    if (!lastSeenAt)
        return 'Noch nie angemeldet';
    const minuten = Math.floor((jetzt - new Date(lastSeenAt).getTime()) / 60_000);
    if (minuten < 1)
        return 'Gerade eben';
    if (minuten < 60)
        return `Vor ${minuten} Min.`;
    const stunden = Math.floor(minuten / 60);
    if (stunden < 24)
        return `Vor ${stunden} Std.`;
    const tage = Math.floor(stunden / 24);
    if (tage === 1)
        return 'Gestern';
    if (tage < 7)
        return `Vor ${tage} Tagen`;
    return 'Länger nicht da';
}
/** Zeitangabe für den Verlauf — dieselbe Sprache wie bei der Anwesenheit. */
export function wannText(zeitpunkt, jetzt = Date.now()) {
    const minuten = Math.floor((jetzt - new Date(zeitpunkt).getTime()) / 60_000);
    if (minuten < 1)
        return 'gerade eben';
    if (minuten < 60)
        return `vor ${minuten} Min.`;
    const stunden = Math.floor(minuten / 60);
    if (stunden < 24)
        return `vor ${stunden} Std.`;
    const tage = Math.floor(stunden / 24);
    return tage === 1 ? 'gestern' : `vor ${tage} Tagen`;
}
//# sourceMappingURL=status.js.map